import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiClock, FiNavigation } = FiIcons;

const Clubhaus = () => {
  const address = "Hirtengasse 1, 90522 Oberasbach";
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  const headerImage = "https://grafiken.trust-franken.de/HP/814.jpg";
  const logoUrl = "https://grafiken.trust-franken.de/HP/Kreuz.png";

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Hero Header Section */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-end overflow-hidden">
        <motion.img 
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2 }}
          src={headerImage} 
          alt="Clubhaus Header" 
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-16">
          <div className="flex flex-col md:flex-row items-center md:items-end justify-between gap-10">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center md:text-left order-2 md:order-1"
            >
              <h1 className="text-6xl md:text-9xl font-black uppercase tracking-tighter leading-none mb-4">
                Clubhaus
              </h1>
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: 100 }}
                transition={{ delay: 0.8, duration: 1 }}
                className="h-2 bg-white mx-auto md:mx-0" 
              />
            </motion.div>

            <motion.img 
              initial={{ opacity: 0, scale: 0.5, rotate: 15 }}
              animate={{ 
                opacity: 1, 
                scale: 1, 
                rotate: 0,
                y: [0, -15, 0]
              }}
              transition={{ 
                duration: 1,
                y: { duration: 3, repeat: Infinity, ease: "easeInOut" }
              }}
              whileHover={{ scale: 1.1, rotate: -5 }}
              src={logoUrl} 
              alt="Trust MC Logo" 
              className="h-40 md:h-64 w-auto drop-shadow-[0_0_50px_rgba(255,255,255,0.2)] order-1 md:order-2"
            />
          </div>
        </div>
      </section>

      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-10">
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, x: 10 }}
                className="bg-gray-900/40 border border-white/5 rounded-[2rem] p-10 backdrop-blur-xl hover:border-white/20 transition-all duration-500 group"
              >
                <div className="flex items-center space-x-6 mb-8">
                  <div className="p-4 bg-white text-black rounded-2xl group-hover:rotate-12 transition-transform">
                    <SafeIcon icon={FiMapPin} className="text-3xl" />
                  </div>
                  <h2 className="text-3xl font-bold uppercase tracking-tight">Anschrift</h2>
                </div>
                <p className="text-3xl text-gray-300 mb-10 leading-tight font-light">
                  Hirtengasse 1<br />
                  <span className="text-white font-black">90522 Oberasbach</span>
                </p>
                <motion.a 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={mapsUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-3 w-full bg-white text-black py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-gray-200 transition-colors shadow-[0_10px_30px_rgba(255,255,255,0.1)]"
                >
                  <SafeIcon icon={FiNavigation} className="text-xl" />
                  <span>Route planen</span>
                </motion.a>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.02, x: 10 }}
                className="bg-gray-900/40 border border-white/5 rounded-[2rem] p-10 backdrop-blur-xl hover:border-white/20 transition-all duration-500 group"
              >
                <div className="flex items-center space-x-6 mb-8">
                  <div className="p-4 bg-white text-black rounded-2xl group-hover:rotate-12 transition-transform">
                    <SafeIcon icon={FiClock} className="text-3xl" />
                  </div>
                  <h2 className="text-3xl font-bold uppercase tracking-tight">Öffnungszeiten</h2>
                </div>
                <p className="text-2xl text-gray-300 leading-relaxed font-light">
                  Jeden <span className="text-white font-black underline decoration-2 underline-offset-8">2. und 4. Freitag</span> im Monat<br />
                  <span className="text-4xl mt-4 block text-white font-black">ab 20:00 Uhr</span>
                </p>
              </motion.div>
            </div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.8, rotate: 2 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="h-[600px] rounded-[3rem] overflow-hidden border border-white/10 shadow-2xl relative group"
            >
              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" />
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2594.8643809637854!2d10.96316267688229!3d49.43003316024107!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479fabe858f62363%3A0xe5493ff6058d7d42!2sHirtengasse%201%2C%2090522%20Oberasbach!5e0!3m2!1sde!2sde!4v1715852000000!5m2!1sde!2sde"
                width="100%" 
                height="100%" 
                style={{ border: 0, filter: 'grayscale(1) invert(1) contrast(1.1)' }} 
                allowFullScreen="" 
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Clubhaus;